// The story spine — "Spider-Team vs. the Sinister Six" (Phase 7).
//
// One CHAPTER per phonics level (chapter.id === LevelId): the villain of that chapter is
// the one "scrambling the words", and mastering the level *defeats* them and unlocks the
// next chapter. This is a SKIN over the existing band/level progression (see engine/
// session.ts nextBand/bandMastered) — no new gating logic. The rich, voiced narration
// lives in storyLines.ts CHAPTER_LINES (listened to, not read), so the story stays rich
// while every word he READS stays decodable.

import type { LevelId } from './sequence';
import { CHAPTER_LINES } from './storyLines';

export interface Villain {
  /** Art slug → public/villains/<id>.png (falls back to emoji). */
  id: string;
  name: string;
  emoji: string;
}

export interface Chapter {
  /** A chapter IS a phonics level. */
  id: LevelId;
  /** Kid-facing chapter number (1-based). */
  order: number;
  /** Short chapter title for the hero card / map. */
  title: string;
  villain: Villain;
  /** Which team member(s) lead this chapter (display + voice flavor). */
  team: string;
  location: string;
  /** Audio clip ids for the intro / victory story cards. */
  introLineId: string;
  victoryLineId: string;
}

const ch = (
  id: LevelId,
  order: number,
  title: string,
  villain: Villain,
  team: string,
  location: string,
): Chapter => ({
  id,
  order,
  title,
  villain,
  team,
  location,
  introLineId: CHAPTER_LINES[id].intro.id,
  victoryLineId: CHAPTER_LINES[id].victory.id,
});

export const CHAPTERS: Chapter[] = [
  ch('cvc', 1, 'Rooftop Trouble', { id: 'goblin', name: 'Green Goblin', emoji: '🟢' }, 'Spidey', 'The Rooftops'),
  ch('digraphs', 2, 'Lab Break-In', { id: 'ock', name: 'Doc Ock', emoji: '🐙' }, 'Spidey + Miles', 'The Lab'),
  ch('blends', 3, 'Street Smash', { id: 'rhino', name: 'Rhino', emoji: '🦏' }, 'Miles', 'The Streets'),
  ch('vce', 4, 'Beach Bury', { id: 'sandman', name: 'Sandman', emoji: '⏳' }, 'Ghost-Spider', 'The Beach'),
  ch('vowel_teams', 5, 'Power Surge', { id: 'electro', name: 'Electro', emoji: '⚡' }, 'Gwen + Miles', 'The Power Plant'),
  ch('r_controlled', 6, 'Sky Heist', { id: 'vulture', name: 'Vulture', emoji: '🦅' }, 'Spidey + Gwen', 'The Skies'),
  ch('diphthongs', 7, 'Final Showdown', { id: 'venom', name: 'Venom', emoji: '🕷️' }, 'The whole team', 'Downtown'),
];

const BY_LEVEL = new Map<LevelId, Chapter>(CHAPTERS.map((c) => [c.id, c]));

/** The chapter for a level (every seeded level has one). */
export function chapterOf(level: LevelId): Chapter | undefined {
  return BY_LEVEL.get(level);
}

/** The chapter after this one (undefined past the finale). */
export function nextChapter(level: LevelId): Chapter | undefined {
  const c = BY_LEVEL.get(level);
  return c ? CHAPTERS.find((x) => x.order === c.order + 1) : undefined;
}
